import { ListOrdered, Plus } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { GlyphTile } from '../../screens/CMS/Layout/HomepageSettings/components/TravelIconPicker';
import { useTravelIcons } from '../../screens/CMS/Layout/HomepageSettings/components/travelIcons';
import {
  MAX_CUSTOM_MENUS, SECTION, customMenuKey, moveItem, newCustomMenu,
} from './siteHeaderModel';
import { SHOW_ON_LABELS, WEBSITE_DEFAULTS, pickText } from './headerDefaults';
import { MENU_BUTTON_ICONS } from './headerIcons';
import { BuiltInTile, RowActions, SectionCard, StatusChip, useHeaderEditor } from './editorControls';
import styles from './HeaderEditor.module.css';

/** Every menu the header has, by its menuOrder key. */
export function menuEntries(sh) {
  const custom = new Map((sh.menus.custom || []).map((m) => [customMenuKey(m.id), m]));
  return (sh.menuOrder || []).map((key) => {
    if (key === 'destinations') {
      return { key, kind: 'destinations', menu: sh.menus.destinations, kindLabel: 'Destinations', fallback: WEBSITE_DEFAULTS.destinationsLabel };
    }
    if (key === 'holidayTypes') {
      return { key, kind: 'holidayTypes', menu: sh.menus.holidayTypes, kindLabel: 'Holiday types', fallback: WEBSITE_DEFAULTS.holidayTypesLabel };
    }
    const menu = custom.get(key);
    return menu ? { key, kind: 'custom', menu, kindLabel: 'Custom menu', fallback: null } : null;
  }).filter(Boolean);
}

export const menuCardId = (key) => (key.startsWith('custom:') ? `menu-${key.slice(7)}` : `menu-${key}`);

/**
 * The order of the dropdown menus (left to right in the bar, top to bottom in the phone menu),
 * and where a custom menu is added.
 */
export default function MenuOrderSection({ siteHeader, set, onJump }) {
  const { showToast } = useToast();
  const { lang } = useHeaderEditor();
  const icons = useTravelIcons();
  const entries = menuEntries(siteHeader);
  const customCount = siteHeader.menus.custom.length;

  const move = (index, dir) => set('menuOrder', (order) => moveItem(order, index, dir));

  const addMenu = () => {
    if (customCount >= MAX_CUSTOM_MENUS) {
      showToast(`The header holds at most ${MAX_CUSTOM_MENUS} custom menus`, 'error');
      return;
    }
    const menu = newCustomMenu(siteHeader.menus.custom.map((m) => m.id));
    set('', (sh) => ({
      ...sh,
      menus: { ...sh.menus, custom: [...sh.menus.custom, menu] },
      menuOrder: [...sh.menuOrder, customMenuKey(menu.id)],
    }));
    showToast('Custom menu added. Give it a label and some rows.', 'success');
    onJump?.(menuCardId(customMenuKey(menu.id)));
  };

  return (
    <SectionCard
      id={SECTION.menus}
      icon={ListOrdered}
      tone="blue"
      title="Menus"
      description="The dropdown menus in the bar, left to right. The phone menu lists them in the same order."
      headerExtra={
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={addMenu}
          disabled={customCount >= MAX_CUSTOM_MENUS}
          title={customCount >= MAX_CUSTOM_MENUS ? `At most ${MAX_CUSTOM_MENUS} custom menus` : 'Add a custom dropdown menu'}
        >
          <Plus size={14} /> Add custom menu
        </button>
      }
    >
      <div className={styles.orderList}>
        {entries.map((e, index) => {
          const name = pickText(e.menu.label, lang, e.fallback) || 'Untitled menu';
          const ButtonIcon = MENU_BUTTON_ICONS[e.kind];
          return (
            <div key={e.key} className={styles.orderRow}>
              <span className={styles.orderIndex}>{index + 1}</span>
              {e.kind === 'custom' && e.menu.glyph
                ? <GlyphTile name={e.menu.glyph} tone="blue" icons={icons} size={34} />
                : <BuiltInTile icon={ButtonIcon} />}
              <button type="button" className={styles.orderName} onClick={() => onJump?.(menuCardId(e.key))}>
                <strong>{name}</strong>
                <span>{e.kindLabel}</span>
              </button>
              <div className={styles.orderChips}>
                <StatusChip tone={e.menu.enabled ? 'green' : 'muted'}>{e.menu.enabled ? 'On' : 'Off'}</StatusChip>
                {e.menu.showOn !== 'all' && <StatusChip tone="blue">{SHOW_ON_LABELS[e.menu.showOn]}</StatusChip>}
              </div>
              <RowActions index={index} count={entries.length} onMove={move} />
            </div>
          );
        })}
      </div>
      <p className={styles.hint}>
        Up to {MAX_CUSTOM_MENUS} custom menus, each with up to 12 rows. A menu with nothing in it is left out of the bar.
      </p>
    </SectionCard>
  );
}
